import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";

import { verifyPmwCheckout } from "@/lib/payments.functions";
import { getStripeEnvironment } from "@/lib/stripe";
import { PREVIEW_MYTHS, TOTAL_MYTHS } from "@/lib/pmw-content";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";

export const Route = createFileRoute("/prove-me-wrong/return")({
  head: () => ({
    meta: [
      { title: "Thank you - Prove Me Wrong" },
      { name: "robots", content: "noindex" },
    ],
  }),
  validateSearch: (search: Record<string, unknown>): { session_id?: string } => ({
    session_id: typeof search.session_id === "string" ? search.session_id : undefined,
  }),
  component: ReturnPage,
});

function ReturnPage() {
  const { session_id } = Route.useSearch();
  const verify = useServerFn(verifyPmwCheckout);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["pmw-verify", session_id],
    enabled: !!session_id,
    queryFn: () => verify({ data: { sessionId: session_id!, environment: getStripeEnvironment() } }),
    retry: 2,
  });

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-4xl px-4 py-16 md:px-8 md:py-20">
        {!session_id && (
          <div className="text-center">
            <h1 className="font-display text-4xl uppercase">No session found.</h1>
            <p className="mt-4 text-muted-foreground">Return to the product page to purchase.</p>
            <Link to="/prove-me-wrong" className="mt-6 inline-block">
              <Button>Back to Prove Me Wrong</Button>
            </Link>
          </div>
        )}

        {session_id && isLoading && (
          <div className="flex flex-col items-center gap-4 py-20 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p>Confirming your payment…</p>
          </div>
        )}

        {session_id && error && (
          <div className="text-center">
            <h1 className="font-display text-3xl uppercase">Something went wrong.</h1>
            <p className="mt-4 text-muted-foreground">We couldn't verify your payment.</p>
            <Button className="mt-6" onClick={() => refetch()}>Try again</Button>
          </div>
        )}

        {data?.status === "pending" && (
          <div className="text-center">
            <h1 className="font-display text-3xl uppercase">Payment processing…</h1>
            <p className="mt-4 text-muted-foreground">Hang tight, this usually takes a few seconds.</p>
            <Button className="mt-6" onClick={() => refetch()}>Refresh</Button>
          </div>
        )}

        {data?.status === "error" && (
          <div className="text-center">
            <h1 className="font-display text-3xl uppercase">Verification error.</h1>
            <p className="mt-4 text-muted-foreground">{data.error}</p>
          </div>
        )}

        {data?.status === "paid" && (
          <>
            <div className="flex flex-col items-center text-center">
              <div className="rounded-full bg-primary/10 p-3 text-primary">
                <CheckCircle2 className="h-10 w-10" />
              </div>
              <div className="mt-6 text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· Unlocked</div>
              <h1 className="mt-3 font-display text-4xl uppercase leading-[0.95] md:text-6xl">
                You're armed. <span className="text-gradient-curiosity">All {TOTAL_MYTHS} myths.</span>
              </h1>
              <p className="mt-6 max-w-xl text-muted-foreground">
                Receipt {data.email ? <>sent to <span className="text-foreground">{data.email}</span></> : "emailed to you"}.
                Bookmark this page - the full list lives here.
              </p>
            </div>

            <div className="mt-16">
              <h2 className="font-display text-2xl uppercase">The full {TOTAL_MYTHS}.</h2>
              <div className="mt-6 space-y-6">
                {[...PREVIEW_MYTHS, ...data.myths].map((m) => (
                  <article key={m.id} className="rounded-xl border border-border bg-background p-5 md:p-6">
                    <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground">
                      Myth #{String(m.id).padStart(2, "0")}
                    </div>
                    <h3 className="mt-2 font-display text-xl uppercase leading-tight md:text-2xl">"{m.claim}"</h3>
                    <p className="mt-4 text-foreground"><span className="font-semibold text-primary">Truth: </span>{m.truth}</p>
                    <p className="mt-2 text-sm text-muted-foreground"><span className="font-semibold">Receipt: </span>{m.receipt}</p>
                    <p className="mt-2 text-sm text-foreground"><span className="font-semibold">Say this: </span>{m.ammo}</p>
                  </article>
                ))}
              </div>
            </div>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
